import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Stack,
  Chip,
  IconButton,
  Collapse,
  Paper,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import CampaignIcon from "@mui/icons-material/Campaign";
import PushPinIcon from "@mui/icons-material/PushPin";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import StarIcon from "@mui/icons-material/Star";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import announcementsApiService, {
  AnnouncementDTO,
} from "../../services/announcements-api";

interface AnnouncementsPanelProps {
  maxHeight?: number | string;
  onAnnouncementsLoaded?: (count: number) => void;
}

const AnnouncementsPanel: React.FC<AnnouncementsPanelProps> = ({
  maxHeight,
  onAnnouncementsLoaded,
}) => {
  const { t } = useTranslation();
  const [announcements, setAnnouncements] = useState<AnnouncementDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());

  useEffect(() => {
    loadAnnouncements();
  }, []);

  const loadAnnouncements = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await announcementsApiService.getActiveAnnouncements();
      setAnnouncements(data);
      // Expand all announcements by default
      setExpandedIds(
        new Set(data.map((a) => a.id!).filter((id) => id !== undefined)),
      );
      // Notify parent of announcement count
      onAnnouncementsLoaded?.(data.length);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : t("announcements.errorLoad"),
      );
      onAnnouncementsLoaded?.(0);
    } finally {
      setLoading(false);
    }
  };

  const toggleExpand = (id: number) => {
    setExpandedIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  // Sort: pinned first, then by priority (IMPORTANT first), then by date
  const sortedAnnouncements = [...announcements].sort((a, b) => {
    if (a.isPinned !== b.isPinned) return a.isPinned ? -1 : 1;
    if (a.priority !== b.priority) return a.priority === "IMPORTANT" ? -1 : 1;
    return new Date(b.startDate).getTime() - new Date(a.startDate).getTime();
  });

  if (loading) {
    return (
      <Card sx={{ height: "100%" }}>
        <CardContent>
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card sx={{ height: "100%" }}>
        <CardContent>
          <Alert severity="error">{error}</Alert>
        </CardContent>
      </Card>
    );
  }

  if (announcements.length === 0) {
    // Return null to hide panel - parent will handle layout adjustment
    return null;
  }

  return (
    <Card
      sx={{
        height: "100%",
        background: (theme) =>
          theme.palette.mode === "dark"
            ? "rgba(255, 255, 255, 0.05)"
            : "rgba(255, 255, 255, 0.8)",
        backdropFilter: "blur(10px)",
        border: 1,
        borderColor: (theme) =>
          theme.palette.mode === "dark"
            ? "rgba(255, 255, 255, 0.1)"
            : "rgba(33, 150, 243, 0.1)",
        borderRadius: 2,
        boxShadow: (theme) =>
          theme.palette.mode === "dark"
            ? "0 8px 32px rgba(0, 0, 0, 0.3)"
            : "0 8px 32px rgba(33, 150, 243, 0.1)",
      }}
    >
      <CardContent
        sx={{
          pt: 2.5,
          pb: 2.5,
          pl: 2.5,
          pr: 0,
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            mb: 2,
            flexShrink: 0,
            pr: 2.5,
          }}
        >
          <CampaignIcon color="primary" />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {t("announcements.title")}
          </Typography>
          <Chip
            label={announcements.length}
            size="small"
            color="primary"
            sx={{ ml: "auto" }}
          />
        </Box>

        <Box
          sx={{ flex: 1, overflowY: "auto", ...(maxHeight && { maxHeight }) }}
        >
          <Stack spacing={1.5} sx={{ pr: 2.5 }}>
            {sortedAnnouncements.map((announcement, index) => (
              <AnnouncementItem
                key={announcement.id}
                announcement={announcement}
                expanded={expandedIds.has(announcement.id!)}
                onToggle={() => toggleExpand(announcement.id!)}
                isLast={index === sortedAnnouncements.length - 1}
              />
            ))}
          </Stack>
        </Box>
      </CardContent>
    </Card>
  );
};

interface AnnouncementItemProps {
  announcement: AnnouncementDTO;
  expanded: boolean;
  onToggle: () => void;
  isLast: boolean;
}

const AnnouncementItem: React.FC<AnnouncementItemProps> = ({
  announcement,
  expanded,
  onToggle,
  isLast,
}) => {
  const { t } = useTranslation();
  const isImportant = announcement.priority === "IMPORTANT";

  return (
    <Paper
      elevation={0}
      sx={{
        p: 1.5,
        borderRadius: 1.5,
        border: 1,
        borderColor: isImportant ? "info.light" : "divider",
        background: (theme) =>
          isImportant
            ? theme.palette.mode === "dark"
              ? "rgba(33, 150, 243, 0.15)"
              : "rgba(33, 150, 243, 0.08)"
            : theme.palette.mode === "dark"
              ? "rgba(255, 255, 255, 0.02)"
              : "rgba(0, 0, 0, 0.01)",
        transition: "all 0.2s ease",
        "&:hover": {
          borderColor: isImportant ? "info.main" : "primary.light",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: 1,
          cursor: "pointer",
        }}
        onClick={onToggle}
      >
        {/* Icons */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 0.5,
            pt: 0.5,
          }}
        >
          {announcement.isPinned && (
            <PushPinIcon fontSize="small" color="primary" />
          )}
          {isImportant && (
            <StarIcon fontSize="small" sx={{ color: "info.main" }} />
          )}
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: expanded ? "normal" : "nowrap",
                color: isImportant ? "info.main" : "text.primary",
              }}
            >
              {announcement.title}
            </Typography>
          </Box>

          {announcement.category && (
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}
            >
              <Chip
                label={announcement.category}
                size="small"
                variant="outlined"
                sx={{ height: 20, fontSize: "0.7rem" }}
              />
            </Box>
          )}

          <Collapse in={expanded}>
            <Box
              sx={{
                mt: 1,
                color: "text.secondary",
                wordBreak: "break-word",
                "& h1, & h2, & h3, & h4, & h5, & h6": {
                  mt: 1.5,
                  mb: 0.5,
                  fontWeight: 600,
                  color: "text.primary",
                },
                "& h1": { fontSize: "1.25rem" },
                "& h2": { fontSize: "1.15rem" },
                "& h3": { fontSize: "1.1rem" },
                "& h4, & h5, & h6": { fontSize: "1rem" },
                "& p": { my: 0.5 },
                "& ul, & ol": { pl: 2, my: 0.5 },
                "& li": { my: 0.25 },
                "& code": {
                  backgroundColor: "action.hover",
                  px: 0.5,
                  py: 0.25,
                  borderRadius: 0.5,
                  fontSize: "0.85em",
                },
                "& pre": {
                  backgroundColor: "action.hover",
                  p: 1,
                  borderRadius: 1,
                  overflow: "auto",
                },
                "& blockquote": {
                  borderLeft: 3,
                  borderColor: "primary.main",
                  pl: 1.5,
                  ml: 0,
                  fontStyle: "italic",
                },
                "& a": {
                  color: "primary.main",
                  textDecoration: "underline",
                },
                "& strong": { fontWeight: 600 },
                "& img": {
                  maxWidth: "100%",
                  borderRadius: 1,
                },
              }}
            >
              <ReactMarkdown rehypePlugins={[rehypeRaw]}>
                {announcement.content || ""}
              </ReactMarkdown>
            </Box>
            {announcement.imageUrl && (
              <Box
                component="img"
                src={announcement.imageUrl}
                alt={announcement.title}
                sx={{
                  mt: 1.5,
                  maxWidth: "100%",
                  maxHeight: 200,
                  borderRadius: 1,
                  objectFit: "contain",
                }}
              />
            )}
          </Collapse>
        </Box>

        {/* Expand/Collapse Button */}
        <IconButton size="small" sx={{ mt: -0.5 }}>
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>
    </Paper>
  );
};

export default AnnouncementsPanel;
